<template>
  <FormHeader :formStep="formStep" @onBackClick="onBack" />
  <FormContainer>
    <TransitionWrapper>
      <FormTitle :class="$style.formTitle">
        {{ $t('propertyOwner.title') }}
      </FormTitle>
      <FormContent>
        <Space size="8px" direction="vertical">
          <FormRadioBoxItem
            :label="$t('answer.yes')"
            name="propertyOwnership"
            value="1"
            :checked="Number(formData.isPropertyOwner).toString()"
            @onClick="onItemClick"
          />
          <FormRadioBoxItem
            :label="$t('answer.no')"
            name="propertyOwnership"
            value="0"
            :checked="Number(formData.isPropertyOwner).toString()"
            @onClick="onItemClick"
          />
        </Space>
      </FormContent>
    </TransitionWrapper>
    <ValuationFunnelErrorText v-if="isShowErrorMessage" />
  </FormContainer>
</template>

<script setup lang="ts">
import {
  FormContainer,
  FormContent,
  FormHeader,
  FormRadioBoxItem,
  FormTitle,
  Space,
  TransitionWrapper,
  ValuationFunnelErrorText,
} from '@/components/common';
import { useTrackingWithTreatment } from '@/composables/useTrackingWithTreatment';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { PropertyValuationData } from '@/stores/types/propertyValuation';
import { getStepInputs, isPropertyDetailDataPrefilled } from '@/tracking/tracking';
import { ValuationFlow } from '@/types';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const propertyValuationStore = usePropertyValuationStore();
const {
  data: formData,
  step: formStep,
  propertyDetailsData,
  selectedPropertyDetailIndex,
} = storeToRefs(propertyValuationStore);
const { locale } = useI18n();
const route = useRoute();
const { trackVirtualPageViewWithTreatment } = useTrackingWithTreatment();
const isShowErrorMessage = ref<boolean>(false);

onMounted(() => {
  trackVirtualPageViewWithTreatment({
    subType: 'input_home_owner',
    locale: locale.value,
    stepInputs: getStepInputs(
      {
        ...formData.value,
        aptDetails: isPropertyDetailDataPrefilled(
          propertyDetailsData.value,
          selectedPropertyDetailIndex.value,
        ),
      },
      (route?.query?.source as any) || '',
      formStep.value,
    ),
  });
});

const onItemClick = (value: string): void => {
  isShowErrorMessage.value = false;
  const isPropertyOwner = value === '1';
  const formPayload: Partial<PropertyValuationData> = {
    isPropertyOwner,
  };
  if (!isPropertyOwner) {
    formPayload.whenToSell = undefined;
  }
  propertyValuationStore.setFormData(formPayload);
  onNext();
};

const onNext = (): void => {
  const { isPropertyOwner } = formData.value;
  if (isPropertyOwner === undefined) {
    isShowErrorMessage.value = true;
    return;
  }

  propertyValuationStore.navigateNext(ValuationFlow.PropertyOwnership);
};

const onBack = (): void => {
  propertyValuationStore.navigateBack(ValuationFlow.PropertyOwnership);
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

.formTitle {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: $space-50;
}
</style>
